import * as posthog from '@posthog/react'
import { addIf } from 'gleam:altar/options.ffi'
import { type Option$ } from 'gleam:gleam/option'
import * as option from 'gleam:gleam/option'
import * as _posthog from 'gleam:posthog'
import * as _ from 'gleam:prelude'
import { type Result, Result$Error, Result$Ok } from 'gleam:prelude'
import posthogJs from 'posthog-js'
import * as react from 'react'

// https://posthog.com/docs/product-analytics/identify
// https://posthog.com/docs/product-analytics/group-analytics
export const useIdentify = (
  distinctId: Option$<string>,
  email: Option$<string>,
  name: Option$<string>,
  orgId: Option$<string>,
  orgName: Option$<string>
): void => {
  const ph = posthog.usePostHog()
  const id = option.unwrap(distinctId, null)
  const emailVal = option.unwrap(email, null)
  const nameVal = option.unwrap(name, null)
  const orgIdVal = option.unwrap(orgId, null)
  const orgNameVal = option.unwrap(orgName, null)
  react.useEffect(() => {
    if (!id || !ph) return
    const userProps: Record<string, string> = {}
    if (emailVal) userProps['email'] = emailVal
    if (nameVal) userProps['name'] = nameVal
    ph.identify(id, userProps)
    if (orgIdVal) {
      const groupProps: Record<string, string> = {}
      if (orgNameVal) groupProps['name'] = orgNameVal
      ph.group('organization', orgIdVal, groupProps)
    }
  }, [ph, id, emailVal, nameVal, orgIdVal, orgNameVal])
}

export const capture = (event: string, properties: object): void => {
  posthogJs.capture(event, properties)
}

export const reset = (): void => {
  posthogJs.reset()
}

export const newException = (): Error => new Error()

export const captureException = (
  exception: Error,
  message: string
): Result<string, undefined> => {
  exception.message = message
  const result = posthogJs.captureException(exception)
  if (result === undefined) {
    return Result$Error(undefined)
  }
  return Result$Ok(result.uuid)
}

export const Provider = (
  apiKey: string,
  options: _posthog.Options,
  children: _.List<react.ReactNode>
): react.ReactNode => {
  const opts = {}
  addIf(opts, 'api_host', _posthog.Options$Options$api_host(options))
  addIf(opts, 'defaults', _posthog.Options$Options$defaults(options))
  addIf(
    opts,
    'capture_exceptions',
    _posthog.Options$Options$capture_exceptions(options)
  )
  // Spread as arguments so React treats them as static children; passing the
  // array as a single child makes React require a `key` on each.
  return react.createElement(
    posthog.PostHogProvider,
    { apiKey, options: opts },
    ...children.toArray()
  )
}
